import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import DashboardButton from './DashboardButton';

export interface CalendarRef {
    reset: () => void;
}

interface CalendarProps {
    initialStartDate?: Date;
    initialEndDate?: Date;
    onDateRangeChange?: (startDate: Date | null, endDate: Date | null) => void;
}

const Calendar = forwardRef<CalendarRef, CalendarProps>(({ 
    initialStartDate, 
    initialEndDate, 
    onDateRangeChange 
}, ref) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [startDate, setStartDate] = useState<Date | null>(initialStartDate || null);
    const [endDate, setEndDate] = useState<Date | null>(initialEndDate || null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [dateRangeModified, setDateRangeModified] = useState(false);

    // Expose reset method to parent components
    useImperativeHandle(ref, () => ({
        reset: () => {
            setStartDate(null);
            setEndDate(null);
            setSelectedDate(null);
            setDateRangeModified(false);
        }
    }));

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    // Navigate to previous month
    const goToPreviousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    // Navigate to next month
    const goToNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const getFirstDayOfMonth = () => {
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
        return firstDay === 0 ? 6 : firstDay - 1;
    };

    // Handle date selection
    const handleDateClick = (day: number, isCurrentMonth: boolean) => {
        const selectedDate = new Date(
            currentDate.getFullYear(),
            isCurrentMonth ? currentDate.getMonth() : (day > 15 ? currentDate.getMonth() - 1 : currentDate.getMonth() + 1),
            day
        );
        
        setSelectedDate(selectedDate);
        
        // If no start date is set, or if both start and end dates are set, start a new range
        if (!startDate || (startDate && endDate)) {
            setStartDate(selectedDate);
            setEndDate(null); // Clear end date to allow range selection
        } 
        // If start date is set but no end date
        else if (startDate && !endDate) {
            // If selected date is before start date, swap them
            if (selectedDate < startDate) {
                setEndDate(startDate);
                setStartDate(selectedDate);
            } else {
                // Otherwise set it as end date (could be same as start date for single day)
                setEndDate(selectedDate);
            }
        }
        
        setDateRangeModified(true);
    };

    // Check if a date is within the selected range
    const isInRange = (day: number, isCurrentMonth: boolean) => {
        if (!startDate || !endDate) return false;
        
        const date = new Date(
            currentDate.getFullYear(),
            isCurrentMonth ? currentDate.getMonth() : (day > 15 ? currentDate.getMonth() - 1 : currentDate.getMonth() + 1),
            day
        );
        
        return date >= startDate && date <= endDate;
    };

    // Check if a date is the start or end of the range, or the selected date when only one date is selected
    const isStartOrEndDate = (day: number, isCurrentMonth: boolean) => {
        if (!startDate) return false;
        
        const date = new Date(
            currentDate.getFullYear(),
            isCurrentMonth ? currentDate.getMonth() : (day > 15 ? currentDate.getMonth() - 1 : currentDate.getMonth() + 1),
            day
        );
        
        return (startDate && date.getTime() === startDate.getTime()) || 
               (endDate && date.getTime() === endDate.getTime()) ||
               (selectedDate && !endDate && date.getTime() === selectedDate.getTime());
    };

    // Apply the selected date range
    const handleApplyDateRange = () => {
        // If only start date is set (no end date), use start date as end date (single day selection)
        const finalEndDate = startDate && !endDate ? startDate : endDate;
        
        if (onDateRangeChange) {
            onDateRangeChange(startDate, finalEndDate);
        }
        setDateRangeModified(false);
    };

    // Clear the selected date range
    const handleClearDateRange = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedDate(null);
        setDateRangeModified(true);
        
        if (onDateRangeChange) {
            onDateRangeChange(null, null);
        }
    };

    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const days = Array.from({length: daysInMonth}, (_, i) => ({ day: i + 1, currMonth: true }));

    // Add days for the start of the month from the previous month
    const daysInLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    const placeholdersStart = Array.from({length: getFirstDayOfMonth()}, (_, i) => ({ day: daysInLastMonth - i, currMonth: false })).reverse();

    // Add days for the end of the month from the next month
    const placeholdersEnd = Array.from({length: 42 - daysInMonth - placeholdersStart.length}, (_, i) => ({ day: i + 1, currMonth: false }));

    // Combine all days
    const allDays = [...placeholdersStart, ...days, ...placeholdersEnd];

    // Break down the calendar days into weeks
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
        weeks.push(allDays.slice(i, i + 7));
    }

    return (
        <Container className="calendar-container">
            <Row className="justify-content-between align-items-center mb-4">
                <Col xs="auto">
                    <AiOutlineLeft 
                        onClick={goToPreviousMonth} 
                        style={{ cursor: 'pointer' }} 
                    />
                </Col>
                <Col xs="auto">
                    <span className='calendar-header'>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
                </Col>
                <Col xs="auto">
                    <AiOutlineRight 
                        onClick={goToNextMonth} 
                        style={{ cursor: 'pointer' }} 
                    />
                </Col>
            </Row>
            <Row className="mb-3">
                <Col className="d-flex justify-content-end">
                    <a 
                        onClick={handleClearDateRange} 
                        className="wind-link" 
                        style={{textDecoration: "none", cursor: "pointer"}}
                    >
                        Clear All
                    </a>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col>
                    <div className="weekday-header d-flex justify-content-between">
                        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, index) => (
                            <div key={index} className="text-center" style={{width: '14%'}}>
                                {day}
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
            {weeks.map((week, weekIndex) => (
                <Row key={weekIndex} className='text-center mb-2'>
                    {week.map((day, dayIndex) => (
                        <Col 
                            key={dayIndex} 
                            className="p-2 calendar-day" 
                            style={{
                                color: day.currMonth ? 'white' : 'grey',
                                backgroundColor: isStartOrEndDate(day.day, day.currMonth) 
                                    ? '#067e93' 
                                    : isInRange(day.day, day.currMonth) 
                                        ? 'rgba(6, 126, 147, 0.3)' 
                                        : 'transparent',
                                cursor: 'pointer',
                                borderRadius: '4px'
                            }}
                            onClick={() => handleDateClick(day.day, day.currMonth)}
                        >
                            {day.day}
                        </Col>
                    ))}
                </Row>
            ))}
            <Row className="mt-4">
                <Col className="d-flex justify-content-center">
                    <DashboardButton text="Apply Date Range" onClick={handleApplyDateRange} disabled={!dateRangeModified} />
                </Col>
            </Row>
        </Container>
    );
});

export default Calendar;
